/**
 * Shared DOM rendering helpers for LiveKit platform modules.
 * All functions create vanilla DOM elements — no framework dependency.
 */
const { Track } = require('livekit-client');

const UI = {
  /**
   * Create a participant tile (video + name overlay + status icons).
   * @param {object} participant - LiveKit Participant
   * @param {object} [opts]
   * @param {boolean} [opts.isLocal=false]
   * @param {boolean} [opts.showVideo=true]
   * @param {string}  [opts.name] - Override display name
   * @returns {HTMLElement}
   */
  createTile(participant, opts = {}) {
    const tile = document.createElement('div');
    tile.className = 'lk-tile' + (opts.isLocal ? ' lk-tile--local' : '');
    tile.dataset.identity = participant.identity;

    // Video container
    const videoWrap = document.createElement('div');
    videoWrap.className = 'lk-tile-video';
    tile.appendChild(videoWrap);

    // Avatar fallback (initials)
    const avatar = document.createElement('div');
    avatar.className = 'lk-tile-avatar';
    const name = opts.name || participant.name || participant.identity;
    avatar.textContent = UI.getInitials(name);
    videoWrap.appendChild(avatar);

    // Name bar
    const nameBar = document.createElement('div');
    nameBar.className = 'lk-tile-name';
    nameBar.textContent = name;
    tile.appendChild(nameBar);

    // Status icons container
    const status = document.createElement('div');
    status.className = 'lk-tile-status';
    tile.appendChild(status);

    return tile;
  },

  /**
   * Attach a video track to a tile.
   * @param {HTMLElement} tile - Tile element
   * @param {object} track - LiveKit Track
   */
  attachTrackToTile(tile, track) {
    const videoWrap = tile.querySelector('.lk-tile-video');
    if (!videoWrap) return;

    if (track.kind === Track.Kind.Video) {
      // Remove any existing video first (idempotent)
      const existing = videoWrap.querySelector('.lk-video-element');
      if (existing) existing.remove();

      const el = track.attach();
      el.className = 'lk-video-element';
      videoWrap.appendChild(el);
      tile.classList.add('lk-tile--has-video');
    }
  },

  /**
   * Detach a video track from a tile.
   */
  detachTrackFromTile(tile, track) {
    if (!tile) return;
    const elements = track.detach();
    for (const el of elements) {
      if (tile.contains(el)) el.remove();
    }
    // Check if any video left
    const videoWrap = tile.querySelector('.lk-tile-video');
    if (videoWrap && !videoWrap.querySelector('.lk-video-element')) {
      tile.classList.remove('lk-tile--has-video');
    }
  },

  /**
   * Attach an audio track (invisible, just plays).
   * @param {object} track - LiveKit audio Track
   * @returns {HTMLAudioElement}
   */
  attachAudioTrack(track) {
    const el = track.attach();
    el.style.display = 'none';
    el.autoplay = true;
    document.body.appendChild(el);
    // Force play (browsers may block autoplay)
    const playPromise = el.play();
    if (playPromise) {
      playPromise.catch(err => {
        console.warn('[LK-UI] Audio autoplay blocked, retrying on user interaction:', err.message);
        const resume = () => {
          el.play().catch(() => {});
          document.removeEventListener('click', resume);
          document.removeEventListener('keydown', resume);
        };
        document.addEventListener('click', resume, { once: true });
        document.addEventListener('keydown', resume, { once: true });
      });
    }
    return el;
  },

  /**
   * Detach an audio track.
   */
  detachAudioTrack(track) {
    const elements = track.detach();
    for (const el of elements) el.remove();
  },

  /**
   * Update mic/camera status icons on a tile.
   * @param {HTMLElement} tile
   * @param {object} status - { micMuted: boolean, cameraMuted: boolean }
   */
  updateTileStatus(tile, status) {
    const container = tile.querySelector('.lk-tile-status');
    if (!container) return;
    container.innerHTML = '';

    if (status.micMuted) {
      const icon = document.createElement('span');
      icon.className = 'lk-status-icon lk-status-mic-off';
      icon.title = 'Microphone off';
      container.appendChild(icon);
    }
    if (status.cameraMuted) {
      const icon = document.createElement('span');
      icon.className = 'lk-status-icon lk-status-cam-off';
      icon.title = 'Camera off';
      container.appendChild(icon);
    }
  },

  /**
   * Set speaking indicator on a tile.
   */
  setTileSpeaking(tile, isSpeaking) {
    if (!tile) return;
    tile.classList.toggle('lk-tile--speaking', isSpeaking);
  },

  /**
   * Create a control bar with action buttons.
   * @param {Array<object>} actions - [{ id, icon, label, className, onClick, toggle }]
   * @returns {HTMLElement}
   */
  createControls(actions) {
    const bar = document.createElement('div');
    bar.className = 'lk-controls';

    for (const action of actions) {
      const btn = document.createElement('button');
      btn.className = 'lk-ctrl-btn' + (action.className ? ' ' + action.className : '');
      btn.dataset.action = action.id;
      btn.title = action.label || '';
      btn.innerHTML = action.icon || '';
      if (action.onClick) btn.addEventListener('click', action.onClick);
      bar.appendChild(btn);
    }

    return bar;
  },

  /**
   * Toggle a control button active state.
   */
  toggleControl(controlsEl, actionId, active) {
    const btn = controlsEl.querySelector(`[data-action="${actionId}"]`);
    if (btn) btn.classList.toggle('lk-ctrl-btn--active', active);
  },

  /**
   * Create a participant row for audio-only mode.
   * @param {object} participant
   * @param {object} [opts]
   * @returns {HTMLElement}
   */
  createAudioParticipantRow(participant, opts = {}) {
    const row = document.createElement('div');
    row.className = 'lk-audio-participant';
    row.dataset.identity = participant.identity;

    // Avatar circle
    const avatar = document.createElement('div');
    avatar.className = 'lk-audio-avatar';
    const name = opts.name || participant.name || participant.identity;
    avatar.textContent = UI.getInitials(name);
    row.appendChild(avatar);

    // Name
    const nameEl = document.createElement('span');
    nameEl.className = 'lk-audio-name';
    nameEl.textContent = name;
    row.appendChild(nameEl);

    // Mic icon
    const mic = document.createElement('span');
    mic.className = 'lk-audio-mic';
    row.appendChild(mic);

    return row;
  },

  /**
   * Show a centered overlay message inside a container.
   */
  showOverlay(container, message) {
    let overlay = container.querySelector('.lk-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'lk-overlay';
      container.appendChild(overlay);
    }
    overlay.textContent = message;
    overlay.style.display = 'flex';
  },

  hideOverlay(container) {
    const overlay = container.querySelector('.lk-overlay');
    if (overlay) overlay.style.display = 'none';
  },

  /**
   * Get initials from a name (max 2 chars).
   */
  getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  },

  /**
   * SVG icons used in controls.
   */
  icons: {
    mic: '<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.93V21h2v-3.07A7 7 0 0 0 19 11h-2z"/></svg>',
    micOff: '<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M19 11h-2a5 5 0 0 1-.78 2.68l1.46 1.46A6.96 6.96 0 0 0 19 11zM12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6c0 .35.06.69.18 1l4.06-4.06L12 14zM3.27 3 2 4.27l6.01 6.01V11a4 4 0 0 0 4 4c.59 0 1.15-.13 1.66-.36l1.52 1.52A6.96 6.96 0 0 1 12 17a7 7 0 0 1-7-6H3a9 9 0 0 0 8 8.94V23h2v-3.06c1.21-.15 2.34-.57 3.32-1.18l4.41 4.41 1.27-1.27L3.27 3z"/></svg>',
    camera: '<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M17 10.5V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.5l4 4v-11l-4 4z"/></svg>',
    cameraOff: '<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M21 6.5l-4 4V7a1 1 0 0 0-1-1H9.82L21 17.18V6.5zM3.27 2 2 3.27 4.73 6H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z"/></svg>',
    screenShare: '<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M20 18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H1v2h22v-2h-3zm-7-3.53v-2.19c-2.78 0-4.61.85-6 2.72.56-2.67 2.11-5.33 6-5.87V7l4 3.73-4 3.74z"/></svg>',
    screenShareOff: '<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M21.79 18l2 2H24v-2h-2.21zM1.11 2.98l1.55 1.56c-.41.37-.66.89-.66 1.48V16c0 1.1.9 2 2 2H0v2h18.13l2.71 2.71 1.41-1.41L2.52 1.57 1.11 2.98zM4 16V6.03L14.12 16H4z"/></svg>',
    hangup: '<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.956.956 0 0 1-.29-.7c0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.1-.7-.28-.79-.73-1.68-1.36-2.66-1.85a.993.993 0 0 1-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/></svg>',
    layout: '<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M3 3h8v8H3V3zm0 10h8v8H3v-8zm10-10h8v8h-8V3zm0 10h8v8h-8v-8z"/></svg>'
  }
};

module.exports = { UI };
